import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import AdminLayout from '../../components/AdminLayout';
import { TrendingUp, Users, DollarSign, PiggyBank, Activity, Wallet, Receipt } from 'lucide-react';

export default function AdminDashboard({ user, onLogout }) {
  const [stats, setStats] = useState(null);
  const [tdsStats, setTdsStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const [statsRes, tdsRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard-stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/tds-summary`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setStats(statsRes.data);
      setTdsStats(tdsRes.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout user={user} onLogout={onLogout} currentPage="dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Loading...</div>
        </div>
      </AdminLayout>
    );
  }

  const statCards = [
    {
      title: 'Active Investors',
      value: stats?.total_investors || 0,
      icon: Users,
      color: 'text-accent',
      bgColor: 'bg-accent/10',
      testId: 'stat-active-investors'
    },
    {
      title: 'Total Investments',
      value: stats?.total_investments || 0,
      icon: Activity,
      color: 'text-success',
      bgColor: 'bg-success/10',
      testId: 'stat-total-investments'
    },
    {
      title: 'Total Invested',
      value: `₹${(stats?.total_invested || 0).toLocaleString('en-IN')}`,
      icon: DollarSign,
      color: 'text-warning',
      bgColor: 'bg-warning/10',
      testId: 'stat-total-invested'
    },
    {
      title: 'Current Value',
      value: `₹${(stats?.total_current_value || 0).toLocaleString('en-IN')}`,
      icon: PiggyBank,
      color: 'text-success',
      bgColor: 'bg-success/10',
      testId: 'stat-current-value'
    },
    {
      title: 'Interest Earned',
      value: `₹${(stats?.total_interest_earned || 0).toLocaleString('en-IN')}`,
      icon: TrendingUp,
      color: 'text-accent',
      bgColor: 'bg-accent/10',
      testId: 'stat-interest-earned'
    },
    {
      title: 'Total Payouts',
      value: `₹${(stats?.total_payouts || 0).toLocaleString('en-IN')}`,
      icon: Wallet,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      testId: 'stat-total-payouts'
    },
  ];

  return (
    <AdminLayout user={user} onLogout={onLogout} currentPage="dashboard">
      <div className="space-y-8" data-testid="admin-dashboard">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Dashboard
          </h1>
          <p className="text-muted-foreground text-lg">Overview of your investment portfolio</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div
                key={index}
                data-testid={stat.testId}
                className="card p-6 stat-card hover:shadow-md"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-2">{stat.title}</p>
                    <p className="text-3xl font-bold font-mono" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      {stat.value}
                    </p>
                  </div>
                  <div className={`${stat.bgColor} ${stat.color} p-3 rounded-lg`}>
                    <Icon className="w-6 h-6" strokeWidth={1.5} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card p-6">
            <h3 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Quick Actions
            </h3>
            <div className="space-y-3">
              <a href="/admin/investors" className="block">
                <button data-testid="quick-action-add-investor" className="w-full btn-primary text-left justify-start">
                  <Users className="w-4 h-4 mr-2 inline" />
                  Manage Investors
                </button>
              </a>
              <a href="/admin/investments" className="block">
                <button data-testid="quick-action-create-investment" className="w-full btn-outline text-left justify-start">
                  <Activity className="w-4 h-4 mr-2 inline" />
                  Manage Investments
                </button>
              </a>
              <a href="/admin/payouts" className="block">
                <button data-testid="quick-action-process-payout" className="w-full btn-outline text-left justify-start">
                  <Wallet className="w-4 h-4 mr-2 inline" />
                  Process Payouts
                </button>
              </a>
            </div>
          </div>

          <div className="card p-6">
            <h3 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Performance Summary
            </h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center pb-3 border-b">
                <span className="text-sm text-muted-foreground">Average Return</span>
                <span className="font-mono font-semibold text-success">
                  {stats?.total_invested > 0 
                    ? ((stats?.total_interest_earned / stats?.total_invested) * 100).toFixed(2) 
                    : 0}%
                </span>
              </div>
              <div className="flex justify-between items-center pb-3 border-b">
                <span className="text-sm text-muted-foreground">Active Portfolio Value</span>
                <span className="font-mono font-semibold">
                  ₹{(stats?.total_current_value || 0).toLocaleString('en-IN')}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Total Disbursed</span>
                <span className="font-mono font-semibold">
                  ₹{(stats?.total_payouts || 0).toLocaleString('en-IN')}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* TDS Summary Section */}
        {tdsStats && (
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Receipt className="w-5 h-5 inline mr-2 text-amber-600" />
                TDS Summary (Tax Deducted at Source)
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm text-muted-foreground mb-1">Total Gross Payouts</p>
                <p className="text-xl font-bold font-mono">
                  ₹{(tdsStats.summary?.total_gross_payouts || 0).toLocaleString('en-IN')}
                </p>
              </div>
              <div className="bg-amber-50 rounded-lg p-4">
                <p className="text-sm text-amber-700 mb-1">Total TDS Deducted</p>
                <p className="text-xl font-bold font-mono text-amber-700">
                  ₹{(tdsStats.summary?.total_tds_deducted || 0).toLocaleString('en-IN')}
                </p>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <p className="text-sm text-green-700 mb-1">Total Net Payouts</p>
                <p className="text-xl font-bold font-mono text-green-700">
                  ₹{(tdsStats.summary?.total_net_payouts || 0).toLocaleString('en-IN')}
                </p>
              </div>
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-sm text-blue-700 mb-1">Investors with Payouts</p>
                <p className="text-xl font-bold font-mono text-blue-700">
                  {tdsStats.summary?.total_investors_with_payouts || 0}
                </p>
              </div>
            </div>
            
            {/* Top TDS Deductions */}
            {tdsStats.investor_wise_tds?.length > 0 && (
              <div>
                <h4 className="font-medium mb-3 text-sm text-muted-foreground">TDS by Investor (Top 5)</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 font-medium">Investor</th>
                        <th className="text-right py-2 font-medium">Gross Payouts</th>
                        <th className="text-right py-2 font-medium">TDS Deducted</th>
                        <th className="text-right py-2 font-medium">Net Payouts</th>
                        <th className="text-center py-2 font-medium">TDS Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tdsStats.investor_wise_tds.slice(0, 5).map((item, idx) => (
                        <tr key={idx} className="border-b last:border-0">
                          <td className="py-2">
                            <span className="font-medium">{item.investor_name}</span>
                            <br />
                            <span className="text-xs text-muted-foreground">{item.pan_number || 'No PAN'}</span>
                          </td>
                          <td className="text-right font-mono">₹{item.total_gross_payouts.toLocaleString('en-IN')}</td>
                          <td className="text-right font-mono text-amber-600">₹{item.total_tds_deducted.toLocaleString('en-IN')}</td>
                          <td className="text-right font-mono text-green-600">₹{item.total_net_payouts.toLocaleString('en-IN')}</td>
                          <td className="text-center">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              item.tds_applicable ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600'
                            }`}>
                              {item.tds_applicable ? `${item.tds_rate}%` : 'No TDS'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}